import { useEffect, useState } from 'react';
import { IconCash, IconPercentage, IconBan, IconInfoCircle } from '@tabler/icons-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { notifications } from '../../lib/notifications';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';

const MODES = [
    { id: 'fixed', Icon: IconCash, labelKey: 'operatorPay.wageFixed' },
    { id: 'percent', Icon: IconPercentage, labelKey: 'operatorPay.wagePercent' },
    { id: 'none', Icon: IconBan, labelKey: 'operatorPay.wageNone' },
];
const PERIODS = [
    { id: 'day', labelKey: 'operatorPay.day' },
    { id: 'week', labelKey: 'operatorPay.week' },
    { id: 'month', labelKey: 'operatorPay.month' },
];

// Keep digits and one dot only (no letters, no minus) — the backend validates again.
const cleanNumber = (v) => {
    const s = String(v).replace(/[^0-9.]/g, '');
    const [head, ...rest] = s.split('.');
    return rest.length ? `${head}.${rest.join('').slice(0, 2)}` : head;
};

/**
 * Owner edits how one operator is paid: a fixed amount per day / week / month, or a percentage
 * of the income that operator entered, or nothing. `link` is the OfficeSpaceOperator row
 * ({ operatorId, operator: { name, phone }, wageType, wageAmount, wagePeriod }).
 */
export default function OperatorWageDialog({ open, onClose, officeSpaceId, link }) {
    const { t } = useTranslation();
    const setOperatorWage = useOfficeSpaceStore((s) => s.setOperatorWage);

    const [mode, setMode] = useState('none');
    const [amount, setAmount] = useState('');
    const [period, setPeriod] = useState('day');
    const [saving, setSaving] = useState(false);

    // Load the operator's current wage each time the dialog opens.
    useEffect(() => {
        if (!open || !link) return;
        setMode(link.wageType === 'fixed' || link.wageType === 'percent' ? link.wageType : 'none');
        setAmount(link.wageAmount != null ? String(link.wageAmount) : '');
        setPeriod(link.wagePeriod || 'day');
    }, [open, link]);

    const handleSave = async () => {
        if (!link || saving) return;
        let payload;
        if (mode === 'none') {
            payload = { wageType: null };
        } else {
            const n = Number(amount);
            if (!amount || !Number.isFinite(n) || n < 0) {
                notifications.show({ message: t('operatorPay.invalidAmount'), color: 'red' });
                return;
            }
            if (mode === 'percent' && n > 100) {
                notifications.show({ message: t('operatorPay.invalidPercent'), color: 'red' });
                return;
            }
            payload = mode === 'fixed'
                ? { wageType: 'fixed', wageAmount: n, wagePeriod: period }
                : { wageType: 'percent', wageAmount: n };
        }
        try {
            setSaving(true);
            await setOperatorWage(officeSpaceId, link.operatorId, payload);
            notifications.show({ title: t('common.success'), message: t('operatorPay.saved'), color: 'green' });
            onClose();
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message || t('common.error'), color: 'red' });
        } finally {
            setSaving(false);
        }
    };

    const inputCls = 'w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 py-2.5 px-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40';

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-lg w-[calc(100vw-24px)] p-6">
                <DialogHeader>
                    <DialogTitle className="text-lg flex items-center gap-2">
                        <span>
                            {t('operatorPay.wageTitle')}
                            {link?.operator?.name ? <span className="font-normal text-zinc-500 dark:text-zinc-400"> — {link.operator.name}</span> : null}
                        </span>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button type="button" aria-label={t('operatorPay.editWageTip')} className="flex-none text-zinc-400 hover:text-primary transition-colors">
                                    <IconInfoCircle size={17} stroke={1.8} />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[260px] text-center leading-snug">
                                {t('operatorPay.editWageTip')}
                            </TooltipContent>
                        </Tooltip>
                    </DialogTitle>
                    {/* Always-visible one-liner so the purpose is clear without hovering */}
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('operatorPay.wageDialogHint')}</p>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-3 gap-1 rounded-xl bg-zinc-100 dark:bg-zinc-800 p-1">
                        {MODES.map(({ id, Icon, labelKey }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setMode(id)}
                                className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-2.5 text-[12.5px] font-semibold transition-colors ${
                                    mode === id
                                        ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm'
                                        : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'
                                }`}
                            >
                                <Icon size={15} stroke={1.9} />
                                {t(labelKey)}
                            </button>
                        ))}
                    </div>

                    {/* Fixed area keeps the dialog the same height in every mode */}
                    <div className="min-h-[150px] flex flex-col gap-3">
                        {mode === 'fixed' && (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('operatorPay.amountLabel')}</label>
                                        <input type="text" inputMode="decimal" maxLength={10} value={amount}
                                            onChange={(e) => setAmount(cleanNumber(e.target.value))}
                                            placeholder="0" className={inputCls} />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('operatorPay.periodLabel')}</label>
                                        <div className="grid grid-cols-3 gap-1">
                                            {PERIODS.map((p) => (
                                                <button key={p.id} type="button" onClick={() => setPeriod(p.id)}
                                                    className={`rounded-lg border px-2 py-2.5 text-[12.5px] font-semibold transition-colors ${
                                                        period === p.id
                                                            ? 'border-primary bg-primary/10 text-primary'
                                                            : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                                                    }`}>
                                                    {t(p.labelKey)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('operatorPay.fixedHint')}</p>
                            </>
                        )}
                        {mode === 'percent' && (
                            <>
                                <div className="flex flex-col gap-1.5 sm:max-w-[50%]">
                                    <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('operatorPay.percentLabel')}</label>
                                    <input type="text" inputMode="decimal" maxLength={6} value={amount}
                                        onChange={(e) => setAmount(cleanNumber(e.target.value))}
                                        placeholder="10" className={inputCls} />
                                </div>
                                <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('operatorPay.percentHint')}</p>
                            </>
                        )}
                        {mode === 'none' && (
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('operatorPay.noWage')}</p>
                        )}
                    </div>

                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={onClose}
                            className="rounded-lg border border-zinc-200 dark:border-zinc-700 px-4 py-2.5 text-[13px] font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                            {t('common.cancel')}
                        </button>
                        <button type="button" onClick={handleSave} disabled={saving}
                            className="inline-flex items-center gap-2 rounded-lg bg-primary hover:bg-primary/90 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors disabled:opacity-60">
                            {saving && <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />}
                            {t('operatorPay.save')}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
